import { AvField, AvForm } from "availity-reactstrap-validation";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    FormGroup,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Input,
    Row,
    Spinner,
    Table,
    UncontrolledAlert,
    Collapse
} from "reactstrap";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DATABASE_VEHICULE_PAGE } from "../../routes/routeConstants";
import { vehiculeActions } from "./vehiculeSlice";
import CurrencyInput from "react-currency-input-field";
import { dataActions } from "../dataSlice";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { deleteDocument, uploadDocument as uploadVehicleDocument } from "./vehiculeService";
import { isVehiclePhoto, resolveDocumentUrl } from "./documentUrl";

const initForm = {
    id: 0,
    referenceCampagne: '',
    categVeh: '',
    reform: 'TECHNIQUE',
    marque: '',
    type: '',
    genre: '',
    modele: '',
    carburant: '',
    numChassis: '',
    nbrCylindres: 0,
    puissFiscale: 0,
    immatriculation: "",
    immanterieur: "",
    firstMec: null,
    mecMaroc: null,
    dateSinistre: null,
    ageMonths: 0,
    ageYears: 0,
    etatVehicule: null,
    usage: "",
    proprietere: "",
    prestataire: null,
    compteur: 0,
    fraisimm: 0,
    ville: null,
    assurance: null,
    entretReg: null,
    valMarche: 0,
    epave: 0,
    devis: 0,
    prixAchat: 0,
    coefCommercial: 0,
    documents: [],
};

const CARBURANT_OPTIONS = [
    { value: "Diesel", label: "Diesel" },
    { value: "Essence", label: "Essence" },
    { value: "Hybrid", label: "Hybride" },
];

const documentLabelKeys = {
    CG: "vehiculef.cgFull",
    CG1: "vehiculef.cg",
    CG2: "vehiculef.cg2",
    FACT: "vehiculef.facture",
    CIN: "vehiculef.cin",
    PHOTO: "vehiculef.photos",
};

const extractedFieldLabelKeys = {
    referenceCampagne: "vehiculef.referenceCampagne",
    marque: "vehiculef.marque",
    type: "vehiculef.type",
    genre: "vehiculef.genre",
    modele: "vehiculef.modele",
    carburant: "vehiculef.carburant",
    numChassis: "vehiculef.numChassis",
    nbrCylindres: "vehiculef.nbrCylindres",
    puissFiscale: "vehiculef.puissFiscale",
    immatriculation: "vehiculef.immatriculation",
    immanterieur: "vehiculef.immanterieur",
    firstMec: "vehiculef.firstMec",
    mecMaroc: "vehiculef.mecMaroc",
    dateSinistre: "vehiculef.dateSinistre",
    usage: "vehiculef.usage",
    proprietere: "vehiculef.proprietere",
    compteur: "vehiculef.compteur",
    prixAchat: "vehiculef.prixAchat",
    remise: "vehiculef.remise",
    fraisimm: "vehiculef.fraisimm",
};

const dateFields = new Set(["firstMec", "mecMaroc", "dateSinistre"]);

const formSteps = [
    { key: "details", icon: "ri-car-line", labelKey: "vehiculef.section_details" },
    { key: "photos", icon: "ri-image-add-line", labelKey: "vehiculef.section_photos" },
    { key: "resume", icon: "ri-clipboard-line", labelKey: "vehiculef.section_resume" },
];

const documentTypesByStep = {
    carteGrise: ["CG", "CG1", "CG2"],
    facture: ["FACT"],
    photos: ["CG", "CG1", "CG2", "FACT", "PHOTO"],
};

const quickFillLabels = {
    marque: ["marque", "brand"],
    categVeh: ["categorie", "catégorie", "categorie vehicule", "catégorie vehicule"],
    modele: ["modele", "modèle", "model"],
    type: ["type"],
    genre: ["genre"],
    carburant: ["carburant", "fuel"],
    numChassis: ["chassis", "num chassis", "numero chassis", "numero de chassis", "numéro châssis", "vin"],
    immatriculation: ["immatriculation", "immat", "registration"],
    immanterieur: ["immatriculation anterieure", "immatriculation antérieure", "immat anterieure", "immat antérieure"],
    firstMec: ["premiere mise en circulation", "première mise en circulation", "first mec", "date premiere circulation"],
    mecMaroc: ["mise en circulation maroc", "mec maroc", "date mec maroc"],
    dateSinistre: ["date sinistre", "sinistre"],
    compteur: ["compteur", "kilometrage", "kilométrage", "km"],
    prixAchat: ["prix achat", "prix d'achat", "achat"],
    usage: ["usage"],
    proprietere: ["proprietaire", "propriétaire", "owner"],
    ville: ["ville", "city"],
    assurance: ["assurance", "compagnie assurance"],
    prestataire: ["prestataire", "entreteneur", "entretien regulier", "entretien régulier"],
    nbrCylindres: ["nombre cylindres", "nbr cylindres", "cylindres"],
    puissFiscale: ["puissance fiscale", "puiss fiscale", "cv"],
    etatVehicule: ["etat vehicule", "état vehicule", "etat du vehicule", "état du vehicule"],
    reform: ["reforme", "réforme", "reform"],
};

const quickFillNumericFields = new Set(["compteur", "prixAchat", "nbrCylindres", "puissFiscale"]);

const quickFillSampleText = `Marque: Renault
Categorie vehicule: CATEG_1
Modele: Clio
Type: VP
Genre: CI
Carburant: Diesel
Num chassis: VF1ABC12345678901
Immatriculation: 12345-A-6
Immatriculation anterieure: 98765-B-6
Premiere mise en circulation: 2018-03-15
Mise en circulation Maroc: 2019-05-20
Date sinistre: 2026-06-10
Etat vehicule: Bon
Usage: Personnel
Propriètaire: Nom Prenom
Ville: Casablanca
Assurance: Wafa Assurance
Prestataire: Garage Central
Compteur: 85000
Nombre cylindres: 4
Puissance fiscale: 6
Prix achat: 120000
Reforme: TECHNIQUE`;

const normalizeLoadedDate = (value) => {
    if (!value) return null;
    const dateValue = String(value);
    const isoMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    return value;
};

const normalizeLoadedOption = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;
    return { id: value };
};

const normalizeLoadedEnum = (value) => {
    if (!value) return "";
    if (typeof value === "object") return value.code || value.name || value.id || "";
    return value;
};

const normalizeVehicleForForm = (data) => ({
    ...initForm,
    ...(data || {}),
    categVeh: normalizeLoadedEnum(data?.categVeh),
    reform: normalizeLoadedEnum(data?.reform) || initForm.reform,
    etatVehicule: normalizeLoadedEnum(data?.etatVehicule),
    firstMec: normalizeLoadedDate(data?.firstMec),
    mecMaroc: normalizeLoadedDate(data?.mecMaroc),
    dateSinistre: normalizeLoadedDate(data?.dateSinistre),
    ville: normalizeLoadedOption(data?.ville),
    assurance: normalizeLoadedOption(data?.assurance),
    prestataire: normalizeLoadedOption(data?.prestataire),
    documents: data?.documents || [],
});

const VehiculeForm = () => {
    const { t } = useTranslation('translation');
    const [formState, setForm] = useState(initForm);
    const { isFetching } = useSelector(state => state.vehicule);
    const { categVH, reform, villes, assurances, entrestruc } = useSelector(state => state.data);
    const [error, setError] = useState(null);
    const [recordError, setRecordError] = useState({ open: false, message: null });
    const [saveConfirm, setSaveConfirm] = useState(false);
    const [deleteDocumentConfirm, setDeleteDocumentConfirm] = useState(null);
    const documentsInputRef = useRef(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [photoViewer, setPhotoViewer] = useState({ open: false, photos: [], index: 0 });
    const [deletingDocumentId, setDeletingDocumentId] = useState(null);
    const [formMode, setFormMode] = useState("light");
    const [quickFillText, setQuickFillText] = useState(quickFillSampleText);
    const [quickFillData, setQuickFillData] = useState({});

    const [currentStep, setCurrentStep] = useState("details");

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const selectedPhotoPreviews = useMemo(() => (
        selectedFiles.filter(item => item.type === "PHOTO").map((item, index) => ({
            id: item.id,
            url: URL.createObjectURL(item.file),
            title: item.file.name,
            source: "selected",
            selectedId: item.id,
        }))
    ), [selectedFiles]);

    useEffect(() => () => {
        selectedPhotoPreviews.forEach((photo) => URL.revokeObjectURL(photo.url));
    }, [selectedPhotoPreviews]);

    const existingPhotoPreviews = useMemo(() => (
        (formState.documents || [])
            .filter(isVehiclePhoto)
            .map((doc) => ({
                id: doc.id || doc.filename || doc.path,
                url: resolveDocumentUrl(doc),
                title: doc.filenameUser || doc.filename || t("vehiculef.photos"),
                source: "saved",
                document: doc,
            }))
    ), [formState.documents, t]);

    const allPhotoPreviews = useMemo(() => (
        [...existingPhotoPreviews, ...selectedPhotoPreviews]
    ), [existingPhotoPreviews, selectedPhotoPreviews]);

    const currentStepDocuments = useMemo(() => {
        const allowedTypes = documentTypesByStep[currentStep] || [];
        return (formState.documents || []).filter(doc => allowedTypes.includes(doc?.type));
    }, [currentStep, formState.documents]);

    const currentStepFileDocuments = useMemo(() => (
        currentStepDocuments.filter(doc => doc?.type !== "PHOTO")
    ), [currentStepDocuments]);

    const resumeDocuments = useMemo(() => {
        const savedDocuments = (formState.documents || []).map(doc => ({
            id: `saved-${doc.id || doc.filename}`,
            label: t(documentLabelKeys[doc.type] || doc.type || "Document"),
            name: doc.filenameUser || doc.filename || "-",
            source: "saved",
            document: doc,
            open: () => window.open(resolveDocumentUrl(doc), "_blank"),
        }));

        const selectedDocuments = selectedFiles.map(({ type, file, id }) => ({
            id,
            label: t(documentLabelKeys[type] || type),
            name: file.name,
            source: "selected",
            open: () => window.open(URL.createObjectURL(file), "_blank"),
        }));

        return [...savedDocuments, ...selectedDocuments];
    }, [formState.documents, selectedFiles, t]);

    const openPhotoViewer = (photoList, index = 0) => {
        if (!photoList.length) return;
        setPhotoViewer({ open: true, photos: photoList, index });
    };

    useEffect(() => {
        if (categVH.length == 0) {
            dispatch(dataActions.loadData())
        }
    }, [])

    useEffect(() => {
        if (id) {
            let payload = {
                id,
                onSuccess: (data) => {
                    setForm(normalizeVehicleForForm(data));
                    setCurrentStep("details");
                    setFormMode("light");
                }
            }
            dispatch(vehiculeActions.find(payload));
        }
    }, [id, dispatch]);

    const currentStepIndex = formSteps.findIndex(step => step.key === currentStep);
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === formSteps.length - 1;

    const goToStep = (stepKey) => {
        setCurrentStep(stepKey);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const goToNextStep = () => {
        if (!isLastStep) {
            goToStep(formSteps[currentStepIndex + 1].key);
        }
    };

    const goToPreviousStep = () => {
        if (!isFirstStep) {
            goToStep(formSteps[currentStepIndex - 1].key);
        }
    };

    const formik = useFormik({
        initialValues: { ...formState },
        enableReinitialize: true,
        onSubmit: (values) => {

            setError(null);

            const isUpdate = values.id > 0;
            const normalizedValues = normalizePayloadDates(values, isUpdate);
            const navigateAfterSave = () => {
                navigate(DATABASE_VEHICULE_PAGE, {
                    state: {
                        successMessage: isUpdate ? "Vehicule mis a jour avec succes." : "Vehicule cree avec succes.",
                    },
                });
            };

            const payload = {
                data: normalizedValues,
                onSuccess: async (vehicle) => {
                    const createdVehicleId = getVehicleIdFromResponse(vehicle);
                    if (!isUpdate && !createdVehicleId) {
                        setRecordError({
                            open: true,
                            message: "Le vehicule n'a pas ete confirme par le serveur: identifiant vehicule introuvable dans la reponse.",
                        });
                        return;
                    }

                    const documentUploads = selectedFiles.map(({ file, type }) => ({ file, type }));

                    if (documentUploads.length === 0) {
                        navigateAfterSave();
                        return;
                    }

                    const vId = isUpdate ? values.id : createdVehicleId;

                    if (!vId) {
                        setRecordError({
                            open: true,
                            message: "Vehicule enregistre, mais impossible d'associer les documents: identifiant vehicule introuvable dans la reponse.",
                        });
                        return;
                    }

	                    try {

	                        await Promise.all(documentUploads.map(({ file, type }) => {
	                            const formData = new FormData();
	                            formData.append("file", file);
	                            formData.append("vId", vId);
	                            formData.append("documentType", type);
	
	                            return uploadVehicleDocument(formData);
	                        }));
	                    } catch (e) {
	                        setRecordError({
	                            open: true,
	                            message: e?.response?.data?.message || "Erreur lors de l'enregistrement des documents.",
	                        });
	                        return;
	                    }

                    navigateAfterSave();
                },
                onError: (error) => {
                    setRecordError({
                        open: true,
                        message: error?.response?.data?.message
                            || error?.response?.data?.body?.message
                            || error?.message
                            || "Erreur lors de l'enregistrement du vehicule.",
                    });
                },
            };

            // Create or update
            if (isUpdate) {
                dispatch(vehiculeActions.update(payload));
            } else {
                dispatch(vehiculeActions.create(payload));
            }
        }
    });

    const requestSave = () => {
        setSaveConfirm(true);
    };

    const confirmSave = () => {
        setSaveConfirm(false);
        formik.handleSubmit();
    };


    const handleChangeAssurance = ({ target }) => {
        formik.setFieldValue("assurance", { id: target.value });
    }

    const handleChangeVille = ({ target }) => {
        formik.setFieldValue("ville", { id: target.value });
    }

    const formatDateAsUserTypes = (value) => {
        const rawValue = String(value || "");
        if (rawValue.includes("/")) {
            const [rawDay = "", rawMonth = "", rawYear = ""] = rawValue.split("/");
            const day = rawDay.replace(/\D/g, "").slice(0, 2);
            const month = rawMonth.replace(/\D/g, "").slice(0, 2);
            const year = rawYear.replace(/\D/g, "").slice(0, 4);

            if (rawYear || rawValue.endsWith("//")) return `${day}/${month}/${year}`;
            if (month.length === 2 && !rawValue.endsWith("/")) return `${day}/${month}/`;
            if (rawValue.endsWith("/") && month) return `${day}/${month}/`;
            if (rawMonth || rawValue.endsWith("/")) return `${day}/${month}`;
            return day;
        }

        const digits = rawValue.replace(/\D/g, "").slice(0, 8);
        if (digits.length === 1 && Number(digits) > 3) return `${digits}/`;
        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        if (digits.length === 7) return `${digits.slice(0, 1)}/${digits.slice(1, 3)}/${digits.slice(3)}`;
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    };

    const formatDateForDisplay = (value) => {
        if (!value) return "";

        const dateValue = String(value);
        const isoMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            return `${Number(isoMatch[3])}/${isoMatch[2]}/${isoMatch[1]}`;
        }

        const slashMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{1,4})$/);
        if (slashMatch) {
            return `${Number(slashMatch[1])}/${slashMatch[2].padStart(2, "0")}/${slashMatch[3]}`;
        }

        return formatDateAsUserTypes(dateValue);
    };

    const formatDateForInput = (value) => {
        if (!value) return "";

        const dateValue = String(value);
        const isoMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

        const slashMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (slashMatch) {
            return `${slashMatch[3]}-${slashMatch[2].padStart(2, "0")}-${slashMatch[1].padStart(2, "0")}`;
        }

        return "";
    };

    const parseFormattedDate = (value) => {
        if (!value) return null;

        const dateValue = String(value);
        const isoDate = new Date(dateValue);
        if (/^\d{4}-\d{2}-\d{2}/.test(dateValue) && !isNaN(isoDate.getTime())) {
            return isoDate;
        }

        const match = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!match) return new Date(dateValue);

        const day = Number(match[1]);
        const month = Number(match[2]);
        const year = Number(match[3]);
        return new Date(year, month - 1, day);
    };

    const toIsoDateString = (value) => {
        if (!value) return null;

        const dateValue = String(value);
        const isoMatch = dateValue.match(/^(\d{4}-\d{2}-\d{2})/);
        if (isoMatch) return isoMatch[1];

        const match = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!match) return value;

        const day = match[1].padStart(2, "0");
        const month = match[2].padStart(2, "0");
        return `${match[3]}-${month}-${day}`;
    };

    const normalizeQuickFillText = (value) => String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();

    const normalizeQuickFillDate = (value) => {
        const dateValue = String(value || "").trim();
        if (!dateValue) return "";

        const isoMatch = dateValue.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
        if (isoMatch) {
            return `${isoMatch[1]}-${isoMatch[2].padStart(2, "0")}-${isoMatch[3].padStart(2, "0")}`;
        }

        const slashMatch = dateValue.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
        if (slashMatch) {
            return `${slashMatch[3]}-${slashMatch[2].padStart(2, "0")}-${slashMatch[1].padStart(2, "0")}`;
        }

        return formatDateForInput(dateValue);
    };

    const findQuickFillField = (label) => {
        const normalizedLabel = normalizeQuickFillText(label);
        return Object.entries(quickFillLabels).find(([, labels]) => (
            labels.some(item => normalizedLabel === normalizeQuickFillText(item))
        ))?.[0];
    };

    const normalizeQuickFillValue = (field, value) => {
        const nextValue = String(value || "").trim();
        if (dateFields.has(field)) return normalizeQuickFillDate(nextValue);
        if (quickFillNumericFields.has(field)) return nextValue.replace(/\s/g, "").replace(",", ".");
        if (field === "etatVehicule") {
            const normalizedValue = normalizeQuickFillText(nextValue);
            if (["bon", "etat 1", "etat_1"].includes(normalizedValue)) return "ETAT_1";
            if (["moyen", "etat 2", "etat_2"].includes(normalizedValue)) return "ETAT_2";
            return nextValue;
        }
        if (field === "reform") {
            const normalizedValue = normalizeQuickFillText(nextValue);
            if (normalizedValue.includes("econom")) return "ECONOMIQUE";
            if (normalizedValue.includes("tech")) return "TECHNIQUE";
            return nextValue.toUpperCase();
        }
        return nextValue;
    };

    const parseQuickFillText = (text) => {
        const detected = {};
        String(text || "")
            .split(/\r?\n|;/)
            .map(line => line.trim())
            .filter(Boolean)
            .forEach((line) => {
                const match = line.match(/^(.+?)\s*[:=-]\s*(.+)$/);
                if (!match) return;

                const field = findQuickFillField(match[1]);
                if (!field) return;

                const value = normalizeQuickFillValue(field, match[2]);
                if (value !== "") detected[field] = value;
            });

        return detected;
    };

    const normalizePayloadDates = (values, isUpdate = false) => ({
        ...values,
        id: isUpdate ? values.id : null,
        categVeh: values.categVeh || null,
        firstMec: toIsoDateString(values.firstMec),
        mecMaroc: toIsoDateString(values.mecMaroc),
        dateSinistre: toIsoDateString(values.dateSinistre),
    });

    const normalizeDateFieldValue = (field, value) => {
        if (!dateFields.has(field)) return value;
        return formatDateForDisplay(value);
    };

    const getApiBody = (response) => response?.data?.body ?? response?.body ?? response?.data ?? response;

    const getVehicleIdFromResponse = (response) => {
        const body = getApiBody(response);
        return body?.id
            ?? body?.vehicule?.id
            ?? body?.vehicle?.id
            ?? body?.data?.id
            ?? body?.body?.id
            ?? response?.id
            ?? response?.body?.id
            ?? response?.data?.id
            ?? response?.data?.body?.id;
    };

    const getSelectedFileType = (file) => file?.type?.startsWith("image/") ? "PHOTO" : "FACT";

    const appendSelectedFiles = (files) => {
        const nextFiles = Array.from(files || []).filter(Boolean).map((file, index) => ({
            id: `selected-${file.name}-${file.size}-${file.lastModified}-${index}-${Date.now()}`,
            file,
            type: getSelectedFileType(file),
        }));
        if (!nextFiles.length) return;
        setSelectedFiles(prev => [...prev, ...nextFiles]);
    };

    const clearSelectedFiles = () => {
        setSelectedFiles([]);
        if (documentsInputRef.current) documentsInputRef.current.value = "";
    };

    const handleUnifiedDocumentChange = (event) => {
        appendSelectedFiles(event.target.files);
        event.target.value = "";
    };

    const handleDropOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDocumentDrop = (event) => {
        handleDropOver(event);
        appendSelectedFiles(event.dataTransfer?.files);
    };

    const removeSelectedFile = (selectedId) => {
        setSelectedFiles(prev => prev.filter(item => item.id !== selectedId));
    };

    const removeSavedDocument = async (doc) => {
        if (!doc?.id || deletingDocumentId) return;
        setDeleteDocumentConfirm(null);
        setDeletingDocumentId(doc.id);
        try {
            await deleteDocument(doc.id);
            setForm(prev => ({
                ...prev,
                documents: (prev.documents || []).filter(item => item.id !== doc.id),
            }));
        } catch (e) {
            setRecordError({
                open: true,
                message: e?.response?.data?.message || "Erreur lors de la suppression du document.",
            });
        } finally {
            setDeletingDocumentId(null);
        }
    };

    const activateDropInput = (ref) => {
        ref.current?.click();
    };

    const handleDropInputKeyDown = (event, ref) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            activateDropInput(ref);
        }
    };

    const calculateAge = (firstMec, dateSinistre) => {
        if (!firstMec) return { years: 0, months: 0 };

        const start = parseFormattedDate(firstMec);
        const end = dateSinistre ? parseFormattedDate(dateSinistre) : new Date();

        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            return { years: 0, months: 0 };
        }

        let years = end.getFullYear() - start.getFullYear();
        let months = end.getMonth() - start.getMonth();

        if (months < 0) {
            years--;
            months += 12;
        }

        // If day of month not reached, adjust month difference
        if (end.getDate() < start.getDate()) {
            months--;
            if (months < 0) {
                years--;
                months += 12;
            }
        }

        return { years: Math.max(years, 0), months: Math.max(months, 0) };
    };

    const updateVehicleAge = (nextValues) => {
        const age = calculateAge(nextValues.firstMec, nextValues.dateSinistre);
        formik.setFieldValue("ageYears", age.years, false);
        formik.setFieldValue("ageMonths", age.months, false);
    };

    const handleDateFieldChange = (field, value) => {
        const nextValues = {
            ...formik.values,
            [field]: value || null,
        };

        formik.setFieldValue(field, value || null, false);
        if (field === "firstMec" || field === "dateSinistre") {
            updateVehicleAge(nextValues);
        }
    };

    useEffect(() => {
        updateVehicleAge(formik.values);
    }, [formik.values.firstMec, formik.values.dateSinistre]);

    const detectQuickFillData = () => {
        setQuickFillData(parseQuickFillText(quickFillText));
    };

    const downloadQuickFillTemplate = () => {
        const blob = new Blob([quickFillSampleText], { type: "text/plain;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "modele-saisie-vehicule.txt");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const findQuickFillOption = (options = [], value, fields = ["description", "libelle", "code", "prestataire", "entretReg"]) => {
        const normalizedValue = normalizeQuickFillText(value);
        if (!normalizedValue) return null;

        return options.find(option => fields.some((field) => {
            const optionValue = option?.[field];
            return optionValue && normalizeQuickFillText(optionValue) === normalizedValue;
        })) || options.find(option => fields.some((field) => {
            const optionValue = option?.[field];
            return optionValue && normalizeQuickFillText(optionValue).includes(normalizedValue);
        })) || null;
    };

    const buildQuickFillValues = () => {
        const nextValues = { ...formik.values };

        Object.entries(quickFillData).forEach(([field, value]) => {
            if (field === "ville") {
                const option = findQuickFillOption(villes, value);
                nextValues.ville = option ? { id: option.id } : nextValues.ville;
                return;
            }

            if (field === "assurance") {
                const option = findQuickFillOption(assurances, value);
                nextValues.assurance = option ? { id: option.id } : nextValues.assurance;
                return;
            }

            if (field === "prestataire") {
                const option = findQuickFillOption(entrestruc, value, ["prestataire", "entretReg"]);
                if (option) {
                    nextValues.prestataire = { id: option.id };
                    nextValues.entretReg = option.entretReg;
                }
                return;
            }

            if (field === "categVeh") {
                const option = findQuickFillOption(categVH, value, ["code", "description"]);
                nextValues.categVeh = option?.code || value;
                return;
            }

            nextValues[field] = value;
        });

        const age = calculateAge(nextValues.firstMec, nextValues.dateSinistre);
        nextValues.ageYears = age.years;
        nextValues.ageMonths = age.months;

        return nextValues;
    };

    const applyQuickFillData = () => {
        const nextValues = buildQuickFillValues();
        formik.setValues(nextValues, false);
        formik.setFieldValue("firstMec", nextValues.firstMec || null, false);
        formik.setFieldValue("mecMaroc", nextValues.mecMaroc || null, false);
        formik.setFieldValue("dateSinistre", nextValues.dateSinistre || null, false);
        setCurrentStep("details");
    };


    const handleChangeCategVH = ({ target }) => {
        formik.setFieldValue('categVeh', categVH.find(d => d.code === target.value)?.code || '');
    };

    const renderPhotoEditorGrid = (photoList, compact = false) => {
        if (photoList.length === 0) return null;

        return (
            <div className={`vehicle-photo-editor-grid ${compact ? "is-compact" : ""}`}>
                {photoList.map((photo, index) => (
                    <div className="vehicle-photo-editor-tile" key={photo.id}>
                        <button
                            type="button"
                            className="vehicle-photo-editor-preview"
                            onClick={() => openPhotoViewer(photoList, index)}
                            aria-label={`Voir ${photo.title}`}
                        >
                            <img src={photo.url} alt={photo.title} />
                            <span className="vehicle-photo-editor-overlay">
                                <i className="ri-eye-line"></i>
                            </span>
                        </button>
                        <div className="vehicle-photo-editor-meta">
                            <span className="vehicle-photo-editor-name" title={photo.title}>{photo.title}</span>
                            <span className={`vehicle-photo-editor-badge ${photo.source === "selected" ? "is-new" : ""}`}>
                                {photo.source === "selected" ? "Nouvelle" : "Enregistree"}
                            </span>
                        </div>
                        {photo.source === "selected" && (
                            <button
                                type="button"
                                className="vehicle-photo-editor-remove"
                                onClick={() => removeSelectedFile(photo.selectedId)}
                                aria-label={`Retirer ${photo.title}`}
                            >
                                <i className="ri-close-line"></i>
                            </button>
                        )}
                        {photo.source === "saved" && photo.document?.id && (
                            <button
                                type="button"
                                className="vehicle-photo-editor-remove"
                                onClick={() => removeSavedDocument(photo.document)}
                                disabled={deletingDocumentId === photo.document.id}
                                aria-label={`Supprimer ${photo.title}`}
                            >
                                <i className={deletingDocumentId === photo.document.id ? "ri-loader-4-line" : "ri-close-line"}></i>
                            </button>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <React.Fragment>
            {isFetching ? (
                <div id="preloader">
                    <div id="status">
                        <div className="spinner">
                            <i className="ri-loader-line spin-icon"></i>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="page-content">
                    <Container fluid={true}>
                        <Card className="page-compact-header mb-3 border-0 shadow-sm">
                            <CardBody>
                                <Row className="align-items-center g-3">
                                    <Col>
                                        <div className="d-flex align-items-center gap-3">
                                            <Button type="button" color="light" onClick={() => navigate(DATABASE_VEHICULE_PAGE)}>
                                                <i className="ri-arrow-left-line me-1"></i>
                                                {t('actions.back')}
                                            </Button>
                                            <div>
                                                <div className="page-inline-breadcrumb mb-2">
                                                    <span>{t("database")}</span>
                                                    <i className="ri-arrow-right-s-line"></i>
                                                    <strong>{t("vehicule")}</strong>
                                                </div>
                                                <h4 className="mb-0">{id > 0 ? t('editvehicule') : t('newvehicule')}</h4>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
		                                                
                            </CardBody>
                        </Card>
                        <Card>
                            <CardBody>
                                <AvForm
                                    className="needs-validation"
                                    onSubmit={(event) => {
                                        event?.preventDefault();
                                        requestSave();
                                    }}
                                >
                                    <div className="d-flex flex-wrap gap-2 mb-4">
                                        <Button
                                            type="button"
                                            color={formMode === "light" ? "primary" : "light"}
                                            className="d-flex align-items-center gap-2"
                                            onClick={() => {
                                                setFormMode("light");
                                                setCurrentStep("details");
                                            }}
                                        >
                                            <i className="ri-edit-box-line"></i>
                                            <span>Formulaire simple</span>
                                        </Button>
                                        <Button
                                            type="button"
                                            color={formMode === "ocr" ? "primary" : "light"}
                                            className="d-flex align-items-center gap-2"
                                            onClick={() => setFormMode("ocr")}
                                        >
                                            <i className="ri-scan-2-line"></i>
                                            <span>Formulaire OCR</span>
                                        </Button>
                                    </div>

                                    {formMode === "ocr" ? (
                                        <Card className="border-0 shadow-sm">
                                            <CardBody>
                                                <UncontrolledAlert color="info" className="mb-0 text-dark">
                                                    <div className="d-flex gap-3">
                                                        <i className="ri-information-line fs-4"></i>
                                                        <div>
                                                            <h5 className="mb-1">Formulaire OCR en preparation</h5>
                                                            <p className="mb-0">
                                                                La creation automatique par OCR n'est pas encore disponible. Utilisez le formulaire simple pour saisir les informations du vehicule et ajouter les photos.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </UncontrolledAlert>
                                            </CardBody>
                                        </Card>
                                    ) : (
                                        <React.Fragment>
                                    <div className="d-flex flex-wrap gap-2 mb-4">
                                        {formSteps.map((step, index) => (
                                            <Button
                                                key={step.key}
                                                type="button"
                                                color={step.key === currentStep ? "primary" : "light"}
                                                className="d-flex align-items-center gap-2"
                                                onClick={() => goToStep(step.key)}
                                            >
                                                <span className="fw-semibold">{index + 1}</span>
                                                <i className={step.icon}></i>
                                                <span>{t(step.labelKey)}</span>
                                            </Button>
                                        ))}
                                    </div>
                                    <UncontrolledAlert color="info" className="text-dark">
                                        <div className="d-flex gap-3">
                                            <i className="ri-information-line fs-4"></i>
                                            <div>
                                                <h5 className="mb-2">Guide de saisie</h5>
                                                <p className="mb-2">
                                                    Remplissez d'abord les informations d'identification du vehicule, puis ajoutez les photos dans l'etape dediee avant de verifier le resume.
                                                </p>
                                                <p className="mb-0">
                                                    L'age du vehicule est calcule automatiquement a partir de la date de premiere mise en circulation. Si une date de sinistre est renseignee, elle sert de date de reference; sinon, le calcul utilise la date du jour.
                                                </p>
                                            </div>
                                        </div>
                                    </UncontrolledAlert>
                                    {!isEditMode && (
                                    <Card className={`mt-4 border-0 shadow-sm ${currentStep !== "details" ? "d-none" : ""}`}>
                                        <CardHeader className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <i className="ri-magic-line me-2"></i>
                                                Assistant de saisie rapide
                                            </div>
                                        </CardHeader>
                                        <CardBody>
                                            <Row className="g-3">
                                                <Col lg="7">
                                                    <Label className="form-label" htmlFor="quickFillText">Coller les informations vehicule</Label>
                                                    <Input
                                                        id="quickFillText"
                                                        type="textarea"
                                                        rows="13"
                                                        value={quickFillText}
                                                        placeholder={quickFillSampleText}
                                                        onChange={(event) => setQuickFillText(event.target.value)}
                                                    />
                                                    <div className="d-flex flex-wrap gap-2 mt-3">
                                                        <Button type="button" color="light" onClick={downloadQuickFillTemplate}>
                                                            <i className="ri-download-2-line me-1"></i>
                                                            Telecharger modele
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            color="light"
                                                            onClick={() => {
                                                                setQuickFillText(quickFillSampleText);
                                                                setQuickFillData({});
                                                            }}
                                                        >
                                                            <i className="ri-file-copy-2-line me-1"></i>
                                                            Recharger modele
                                                        </Button>
                                                        <Button type="button" color="info" onClick={detectQuickFillData} disabled={!quickFillText.trim()}>
                                                            <i className="ri-search-eye-line me-1"></i>
                                                            Detecter
                                                        </Button>
                                                        <Button type="button" color="primary" onClick={applyQuickFillData} disabled={Object.keys(quickFillData).length === 0}>
                                                            <i className="ri-check-line me-1"></i>
                                                            Remplir le formulaire
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            color="light"
                                                            onClick={() => {
                                                                setQuickFillText("");
                                                                setQuickFillData({});
                                                            }}
                                                        >
                                                            <i className="ri-close-line me-1"></i>
                                                            Effacer
                                                        </Button>
                                                    </div>
                                                </Col>
                                                <Col lg="5">
                                                    <div className="border rounded p-3 h-100 bg-light quick-fill-detected-card">
                                                        <h6 className="mb-3">Champs detectes</h6>
                                                        <div className="quick-fill-detected-scroll">
                                                            {Object.keys(quickFillData).length > 0 ? (
                                                                <Table responsive size="sm" className="mb-0 align-middle">
                                                                    <tbody>
                                                                        {Object.entries(quickFillData).map(([field, value]) => (
                                                                            <tr key={field}>
                                                                                <td className="text-muted">{t(extractedFieldLabelKeys[field] || `vehiculef.${field}`)}</td>
                                                                                <td className="fw-semibold text-break">{value}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            ) : (
                                                                <p className="text-muted mb-0">
                                                                    Modifiez le modele a gauche, cliquez sur Detecter, puis appliquez les champs trouves.
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                    )}
                                    {/* Documents upload */}
                                    <Card className={`mt-4 ${currentStep !== "photos" ? "d-none" : ""}`}>
                                        <CardHeader
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <i className="ri-car-line me-2"></i>
                                                Documents et photos
                                            </div>
                                        </CardHeader>
                                        <Collapse isOpen={currentStep === "photos"}>
                                            <CardBody>
                                                <Row>
                                                    <Col md="12">
                                                        <div className="vehicle-photo-manager">
                                                            <div className="vehicle-photo-manager-header">
                                                                <div>
                                                                    <Label className="form-label mb-1" htmlFor="vehicleDocuments">Documents et photos</Label>
                                                                    <div className="text-muted small">
                                                                        {formState.documents?.length || 0} enregistres - {selectedFiles.length} nouveaux
                                                                    </div>
                                                                </div>
                                                                {selectedFiles.length > 0 && (
                                                                    <Button size="sm" type="button" color="light" onClick={clearSelectedFiles}>
                                                                        <i className="ri-delete-bin-2-line me-1"></i>
                                                                        Retirer nouveaux fichiers
                                                                    </Button>
                                                                )}
                                                            </div>
                                                            <div
                                                                className="vehicle-photo-drop-panel"
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => activateDropInput(documentsInputRef)}
                                                                onKeyDown={(event) => handleDropInputKeyDown(event, documentsInputRef)}
                                                                onDragOver={handleDropOver}
                                                                onDragEnter={handleDropOver}
                                                                onDrop={handleDocumentDrop}
                                                            >
                                                                <input
                                                                    id="vehicleDocuments"
                                                                    accept="application/pdf,image/*"
                                                                    type="file"
                                                                    multiple
                                                                    className="vehicle-drop-input"
                                                                    ref={documentsInputRef}
                                                                    onChange={handleUnifiedDocumentChange}
                                                                />
                                                                <span className="vehicle-photo-drop-icon"><i className="ri-upload-cloud-2-line"></i></span>
                                                                <span>
                                                                    <strong>{selectedFiles.length > 0 ? `${selectedFiles.length} fichier(s) selectionne(s)` : "Ajouter documents et photos"}</strong>
                                                                    <small>PDF, images carte grise, facture et photos vehicule.</small>
                                                                </span>
                                                            </div>
                                                            {selectedFiles.length > 0 && (
                                                                <div className="border rounded p-3 bg-light">
                                                                    <div className="fw-semibold mb-2">Fichiers selectionnes</div>
                                                                    <div className="d-flex flex-wrap gap-2">
                                                                        {selectedFiles.map(({ id, file, type }) => (
                                                                            <span key={id} className="vehicle-photo-editor-badge is-new d-inline-flex align-items-center gap-1">
                                                                                <i className={type === "PHOTO" ? "ri-image-line" : "ri-file-pdf-line"}></i>
                                                                                {file.name}
                                                                                <button type="button" className="btn btn-sm p-0 text-danger" onClick={() => removeSelectedFile(id)}>
                                                                                    <i className="ri-close-line"></i>
                                                                                </button>
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {allPhotoPreviews.length > 0 ? (
                                                                renderPhotoEditorGrid(allPhotoPreviews)
                                                            ) : (
                                                                <div className="vehicle-photo-empty-state">
                                                                    <i className="ri-image-line"></i>
                                                                    <span>Aucune photo ajoutee.</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Col>
                                                </Row>
		                                                
                                                    {currentStepFileDocuments.length > 0 && (
                                                        <Row className="mt-3">
                                                            {currentStepFileDocuments.map((doc) => (
                                                                <Col md="6" lg="4" key={doc.id || doc.filename} className="mb-3">
                                                                    <Card className="shadow-sm border h-100">
                                                                        <CardBody>
                                                                            <div className="d-flex align-items-center mb-3">
                                                                                {doc.fileType === "application/pdf" ? (
                                                                                    <i className="ri-file-pdf-line text-danger" style={{ fontSize: 30 }}></i>
                                                                                ) : (
                                                                                    <i className="ri-image-line text-info" style={{ fontSize: 30 }}></i>
                                                                                )}
                                                                                <div className="ms-2 min-w-0">
                                                                                    <h6 className="mb-0 text-truncate">{doc.filenameUser || doc.filename}</h6>
                                                                                    <small className="text-muted">{doc.createdDate}</small>
                                                                                </div>
                                                                            </div>

                                                                            <div className="d-flex justify-content-between gap-2">
                                                                                <Button
                                                                                    size="sm"
                                                                                    color="info"
                                                                                    onClick={() => window.open(resolveDocumentUrl(doc), "_blank")}
                                                                                >
                                                                                    <i className="ri-eye-line me-1"></i>
                                                                                    {t('actions.view')}
                                                                                </Button>
                                                                                <Button
                                                                                    size="sm"
                                                                                    color="light"
                                                                                    className="text-danger"
                                                                                    disabled={deletingDocumentId === doc.id}
                                                                                    onClick={() => setDeleteDocumentConfirm(doc)}
                                                                                >
                                                                                    <i className={deletingDocumentId === doc.id ? "ri-loader-4-line me-1" : "ri-delete-bin-2-line me-1"}></i>
                                                                                    Supprimer
                                                                                </Button>
                                                                            </div>
                                                                        </CardBody>
                                                                    </Card>
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    )}

	                                            </CardBody>
                                        </Collapse>
                                    </Card>
                                    {/* Basic vehicle fields */}


                                    {/* ===================== SECTION CHASSIS & IDENTIFICATION ===================== */}
                                    <Card className={`mt-4 ${currentStep !== "details" ? "d-none" : ""}`}>
                                        <CardHeader
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <i className="ri-car-line me-2"></i>
                                                {t('vehiculef.section_chassis')}
                                            </div>
                                        </CardHeader>
                                        <Collapse isOpen={currentStep === "details"}>
                                            <CardBody>
                                                <Row className='mt-3'>
                                                    <Col md="4">
                                                        <Label className="form-label" htmlFor="referenceCampagne">{t('vehiculef.referenceCampagne')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('referenceCampagne')}
                                                            placeholder={t('vehiculef.referenceCampagne')}
                                                            type="text"
                                                            className="form-control"
                                                            id="referenceCampagne"
                                                        />
                                                    </Col>

                                                    <Col md="4">
                                                        <Label htmlFor="categorie" className="form-label">{t('parametrage.categorieV')}</Label>
                                                        <select
                                                            className="form-control"
                                                            id='categorie'
                                                            {...formik.getFieldProps('categVeh')}
                                                            onChange={handleChangeCategVH}
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            {categVH.map((option, index) =>
                                                                <option key={index} value={option.code}>
                                                                    {option.description}
                                                                </option>
                                                            )}
                                                        </select>
                                                        {error && <p style={{ color: "red" }}>{error}</p>}
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="marque">{t('vehiculef.marque')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('marque')}
                                                            placeholder={t('vehiculef.marque')}
                                                            type="text"
                                                            errorMessage={t('message.required')}
                                                            className="form-control"
                                                            id="marque"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="type">{t('vehiculef.type')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('type')}
                                                            placeholder={t('vehiculef.type')}
                                                            type="text"
                                                            errorMessage={t('message.required')}
                                                            className="form-control"
                                                            id="type"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="genre">{t('vehiculef.genre')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('genre')}
                                                            placeholder={t('vehiculef.genre')}
                                                            type="text"
                                                            className="form-control"
                                                            id="genre"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="modele">{t('vehiculef.modele')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('modele')}
                                                            placeholder={t('vehiculef.modele')}
                                                            type="text"
                                                            className="form-control"
                                                            id="modele"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="carburant">{t('vehiculef.carburant')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('carburant')}
                                                            errorMessage={t('message.required')}
                                                            type="select"
                                                            className="form-control"
                                                            id="carburant"
                                                            required
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            {CARBURANT_OPTIONS.map(option => (
                                                                <option key={option.value} value={option.value}>{option.label}</option>
                                                            ))}
                                                        </AvField>
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="numChassis">{t('vehiculef.numChassis')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('numChassis')}
                                                            placeholder={t('vehiculef.numChassis')}
                                                            errorMessage={t('message.required')}
                                                            type="text"
                                                            className="form-control"
                                                            id="numChassis"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="nbrCylindres">{t('vehiculef.nbrCylindres')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('nbrCylindres')}
                                                            placeholder={t('vehiculef.nbrCylindres')}
                                                            errorMessage={t('message.required')}
                                                            type="number"
                                                            className="form-control"
                                                            id="nbrCylindres"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="puissFiscale">{t('vehiculef.puissFiscale')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('puissFiscale')}
                                                            placeholder={t('vehiculef.puissFiscale')}
                                                            errorMessage={t('message.required')}
                                                            type="number"
                                                            className="form-control"
                                                            id="puissFiscale"
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="immatriculation">{t('vehiculef.immatriculation')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('immatriculation')}
                                                            id="immatriculation"
                                                            type="text"
                                                            className="form-control"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="immanterieur">{t('vehiculef.immanterieur')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('immanterieur')}
                                                            id="immanterieur"
                                                            type="text"
                                                            className="form-control"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="firstMec">{t('vehiculef.firstMec')}</Label>
                                                        <Input
                                                            key={`firstMec-${formatDateForInput(formik.values.firstMec)}`}
                                                            name="firstMec"
                                                            id="firstMec"
                                                            type="date"
                                                            value={formatDateForInput(formik.values.firstMec)}
                                                            onChange={(event) => handleDateFieldChange("firstMec", event.target.value)}
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="mecMaroc">{t('vehiculef.mecMaroc')}</Label>
                                                        <Input
                                                            key={`mecMaroc-${formatDateForInput(formik.values.mecMaroc)}`}
                                                            name="mecMaroc"
                                                            id="mecMaroc"
                                                            type="date"
                                                            value={formatDateForInput(formik.values.mecMaroc)}
                                                            onChange={(event) => handleDateFieldChange("mecMaroc", event.target.value)}
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="compteur">{t('vehiculef.compteur')} (km)</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('compteur')}
                                                            id="compteur"
                                                            type="number"
                                                            className="form-control"
                                                        />
                                                    </Col>


                                                </Row>
                                            </CardBody>
                                        </Collapse>
                                    </Card>

                                    {/* ===================== SECTION SINISTRE ===================== */}
                                    <Card className={`mt-4 ${currentStep !== "details" ? "d-none" : ""}`}>
                                        <CardHeader
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <i className="ri-alert-line me-2"></i>
                                                {t('vehiculef.section_sinistre')}
                                            </div>
                                        </CardHeader>
                                        <Collapse isOpen={currentStep === "details"}>
                                            <CardBody>
                                                <Row>
                                                    <Col md="4">
                                                        <Label className="form-label" htmlFor="dateSinistre">{t('vehiculef.dateSinistre')}</Label>
                                                        <Input
                                                            key={`dateSinistre-${formatDateForInput(formik.values.dateSinistre)}`}
                                                            name="dateSinistre"
                                                            id="dateSinistre"
                                                            type="date"
                                                            value={formatDateForInput(formik.values.dateSinistre)}
                                                            onChange={(event) => handleDateFieldChange("dateSinistre", event.target.value)}
                                                        />
                                                        <small className="text-muted">
                                                            Reference du calcul d'age. Si vide, la date du jour est utilisee.
                                                        </small>
                                                    </Col>
                                                    <Col md="2">
                                                        <Label className="form-label" htmlFor="ageYears">{t('vehiculef.ageYears')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('ageYears')}
                                                            id="ageYears"
                                                            type="number"
                                                            className="form-control"
                                                            disabled
                                                        />
                                                    </Col>
                                                    <Col md="2">
                                                        <Label className="form-label" htmlFor="ageMonths">{t('vehiculef.ageMonths')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('ageMonths')}
                                                            id="ageMonths"
                                                            type="number"
                                                            className="form-control"
                                                            disabled
                                                        />
                                                    </Col>
                                                    <Col md="4">
                                                        <Label className="form-label" htmlFor="etatVehicule">{t('vehiculef.etatVehicule')}</Label>
                                                        <select
                                                            className="form-control"
                                                            id='etatVehicule'
                                                            {...formik.getFieldProps('etatVehicule')}
                                                            onChange={(e) => formik.setFieldValue('etatVehicule', e.target.value)}
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            <option value="ETAT_1">Bon</option>
                                                            <option value="ETAT_2">Moyen</option>
                                                        </select>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Collapse>
                                    </Card>

                                    {/* ===================== SECTION ADMINISTRATIF ===================== */}
                                    <Card className={`mt-4 ${currentStep !== "details" ? "d-none" : ""}`}>
                                        <CardHeader
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <i className="ri-file-list-line me-2"></i>
                                                {t('vehiculef.section_admin')}
                                            </div>
                                        </CardHeader>
                                        <Collapse isOpen={currentStep === "details"}>
                                            <CardBody>
                                                <Row>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="usage">{t('vehiculef.usage')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('usage')}
                                                            id="usage"
                                                            type="text"
                                                            className="form-control"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="proprietere">{t('vehiculef.proprietere')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('proprietere')}
                                                            id="proprietere"
                                                            type="text"
                                                            className="form-control"
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="ville">{t('vehiculef.ville')}</Label>
                                                        <select
                                                            className="form-control"
                                                            id='ville'
                                                            {...formik.getFieldProps('ville')}
                                                            value={formik.values.ville?.id || ""}
                                                            onChange={handleChangeVille}
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            {villes.map((option, index) =>
                                                                <option key={index} value={option.id}>
                                                                    {option.description}
                                                                </option>
                                                            )}
                                                        </select>
                                                        {error && <p style={{ color: "red" }}>{error}</p>}
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="assurance">{t('vehiculef.assurance')}</Label>

                                                        <select
                                                            className="form-control"
                                                            id='assurance'
                                                            {...formik.getFieldProps('assurance')}
                                                            value={formik.values.assurance?.id || ""}
                                                            onChange={handleChangeAssurance}
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            {assurances.map((option, index) =>
                                                                <option key={index} value={option.id}>
                                                                    {option.description}
                                                                </option>
                                                            )}
                                                        </select>
                                                        {error && <p style={{ color: "red" }}>{error}</p>}
                                                    </Col>

                                                    <Col md="4">
                                                        <Label className="form-label" htmlFor="entretReg">{t('vehiculef.entretReg')}</Label>
                                                        <select
                                                            className="form-control"
                                                            id='prestataire'
                                                            {...formik.getFieldProps('prestataire')}
                                                            value={formik.values.prestataire?.id || ""}
                                                            onChange={({ target }) => {
                                                                if (target.value) {
                                                                    formik.setFieldValue('prestataire', { id: target.value });
                                                                    const struc = entrestruc.find(e => e.id == target.value);
                                                                    formik.setFieldValue('entretReg', struc.entretReg);
                                                                }
                                                            }}
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            {entrestruc.map((option, index) =>
                                                                <option key={index} value={option.id}>
                                                                    {`${option.entretReg} - ${option.prestataire}`}
                                                                </option>
                                                            )}
                                                        </select>
                                                        {error && <p style={{ color: "red" }}>{error}</p>}
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Collapse>
                                    </Card>

                                    {/* ===================== SECTION VALEURS & FINANCE ===================== */}
                                    <Card className={`mt-4 mb-4 ${currentStep !== "details" ? "d-none" : ""}`}>
                                        <CardHeader
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <i className="ri-money-dollar-circle-line me-2"></i>
                                                {t('vehiculef.section_valeurs')}
                                            </div>
                                        </CardHeader>
                                        <Collapse isOpen={currentStep === "details"}>
                                            <CardBody>
                                                <Row>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="prixAchat">{t('vehiculef.prixAchat')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('prixAchat')}
                                                            placeholder={t('vehiculef.prixAchat')}
                                                            errorMessage={t('message.required')}
                                                            type="number"
                                                            className="form-control"
                                                            id="prixAchat"
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="valMarche">{t('vehiculef.valMarche')}</Label>
                                                        <CurrencyInput
                                                            id="valMarche"
                                                            name="valMarche"
                                                            className="form-control"
                                                            value={formik.values.valMarche}
                                                            decimalsLimit={2}
                                                            onValueChange={(v) => formik.setFieldValue("valMarche", v)}
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="epave">{t('vehiculef.epave')}</Label>
                                                        <CurrencyInput
                                                            id="epave"
                                                            name="epave"
                                                            className="form-control"
                                                            value={formik.values.epave}
                                                            decimalsLimit={2}
                                                            onValueChange={(v) => formik.setFieldValue("epave", v)}
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="devis">{t('vehiculef.devis')}</Label>
                                                        <CurrencyInput
                                                            id="devis"
                                                            name="devis"
                                                            className="form-control"
                                                            value={formik.values.devis}
                                                            decimalsLimit={2}
                                                            onValueChange={(v) => formik.setFieldValue("devis", v)}
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="fraisimm">{t('vehiculef.fraisimm')}</Label>
                                                        <CurrencyInput
                                                            id="fraisimm"
                                                            name="fraisimm"
                                                            className="form-control"
                                                            value={formik.values.fraisimm}
                                                            decimalsLimit={2}
                                                            onValueChange={(v) => formik.setFieldValue("fraisimm", v)}
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="remise">{t('vehiculef.remise')}</Label>
                                                        <CurrencyInput
                                                            id="remise"
                                                            name="remise"
                                                            className="form-control"
                                                            value={formik.values.remise}
                                                            decimalsLimit={2}
                                                            onValueChange={(v) => formik.setFieldValue("remise", v)}
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="remise">{t('vehiculef.coefCommercial')}</Label>
                                                        <CurrencyInput
                                                            id="coefCommercial"
                                                            name="coefCommercial"
                                                            className="form-control"
                                                            value={formik.values.coefCommercial}
                                                            decimalsLimit={2}
                                                            onValueChange={(v) => formik.setFieldValue("coefCommercial", v)}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col md="12">
                                                        {reform?.map((item, index) => (
                                                            <FormGroup check inline key={index}>
                                                                <div className="mb-3 form-check">
                                                                    <AvField
                                                                        type="radio"
                                                                        name="reform"
                                                                        value={item.code}
                                                                        className="form-check-input"
                                                                        id={`reform_${index}`}
                                                                        checked={item.code === formik.values.reform}
                                                                        onChange={() => formik.setFieldValue('reform', item.code)}
                                                                    />
                                                                    <label className="form-check-label" htmlFor={`reform_${index}`}>
                                                                        {t(item.description)}
                                                                    </label>
                                                                </div>
                                                            </FormGroup>
                                                        ))}
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Collapse>
                                    </Card>

                                    <Card className={`mt-4 mb-4 ${currentStep !== "resume" ? "d-none" : ""}`}>
                                        <CardHeader className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <i className="ri-clipboard-line me-2"></i>
                                                {t('vehiculef.section_resume')}
                                            </div>
                                        </CardHeader>
                                        <Collapse isOpen={currentStep === "resume"}>
                                            <CardBody>
                                                <Row>
                                                    {[
                                                        "referenceCampagne",
                                                        "marque",
                                                        "modele",
                                                        "type",
                                                        "immatriculation",
                                                        "numChassis",
                                                        "firstMec",
                                                        "compteur",
                                                        "prixAchat",
                                                        "remise",
                                                        "fraisimm",
                                                        "valMarche",
                                                        "ville",
                                                        "assurance",
                                                    ].map((field) => {
                                                        const rawValue = formik.values[field];
                                                        const value = rawValue && typeof rawValue === "object"
                                                            ? rawValue.description || rawValue.libelle || rawValue.id
                                                            : rawValue;

                                                        return (
                                                            <Col md="4" key={field} className="mb-3">
                                                                <div className="text-muted small">{t(extractedFieldLabelKeys[field] || `vehiculef.${field}`)}</div>
                                                                <div className="fw-semibold">{value || "-"}</div>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                                <Row className="mt-3">
                                                    <Col md="4" className="mb-3">
                                                        <div className="text-muted small">{t('vehiculef.documents')}</div>
                                                        <div className="fw-semibold">{resumeDocuments.length} fichier(s)</div>
                                                    </Col>
                                                    <Col md="4" className="mb-3">
                                                        <div className="text-muted small">{t('vehiculef.photos')}</div>
                                                        <div className="fw-semibold">{allPhotoPreviews.length} photo(s)</div>
                                                    </Col>
	                                                </Row>

                                                    <h6 className="mt-4 mb-3">{t('vehiculef.documents')}</h6>
                                                    {resumeDocuments.length > 0 ? (
                                                        <Table responsive size="sm" className="align-middle">
                                                            <thead>
                                                                <tr>
                                                                    <th>Type</th>
                                                                    <th>Fichier</th>
                                                                    <th>Statut</th>
                                                                    <th className="text-end">Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {resumeDocuments.map((doc) => (
                                                                    <tr key={doc.id}>
                                                                        <td>{doc.label}</td>
                                                                        <td>{doc.name}</td>
                                                                        <td>
                                                                            <span className={`vehicle-photo-editor-badge ${doc.source === "selected" ? "is-new" : ""}`}>
                                                                                {doc.source === "selected" ? "Nouvelle" : "Enregistree"}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <Button size="sm" color="light" onClick={doc.open}>
                                                                                <i className="ri-eye-line me-1"></i>
                                                                                {t('actions.view')}
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    ) : (
                                                        <p className="text-muted mb-0">Aucun document attache.</p>
                                                    )}

                                                    {allPhotoPreviews.length > 0 && <h6 className="mt-4 mb-3">{t('vehiculef.photos')}</h6>}
	                                                {renderPhotoEditorGrid(allPhotoPreviews, true)}
	                                            </CardBody>
                                        </Collapse>
                                    </Card>

                                    <div className="d-flex justify-content-between align-items-center mt-4">
                                        <Button
                                            type="button"
                                            color="light"
                                            disabled={isFirstStep}
                                            onClick={goToPreviousStep}
                                        >
                                            <i className="ri-arrow-left-line me-1"></i>
                                            {t('actions.previous')}
                                        </Button>
                                        <div className="d-flex gap-2">
                                            <Button type="button" color="secondary" onClick={() => navigate(DATABASE_VEHICULE_PAGE)}>
                                                {t('actions.cancel')}
                                            </Button>
                                            {!isLastStep && (
                                                <Button type="button" color="info" onClick={goToNextStep}>
                                                    {t('actions.next')}
                                                    <i className="ri-arrow-right-line ms-1"></i>
                                                </Button>
                                            )}
                                            <Button color="primary" type="button" onClick={requestSave}>
                                                {t('actions.save')}
                                            </Button>
                                        </div>
                                    </div>
                                        </React.Fragment>
                                    )}
                                </AvForm>
                            </CardBody>
                        </Card>
                    </Container>
                </div>
            )}

            <Modal
                isOpen={Boolean(deleteDocumentConfirm)}
                toggle={() => setDeleteDocumentConfirm(null)}
                centered
            >
                <ModalHeader toggle={() => setDeleteDocumentConfirm(null)}>
                    Confirmer la suppression
                </ModalHeader>
                <ModalBody>
                    Voulez-vous supprimer ce document ? Cette action est irréversible.
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="light" onClick={() => setDeleteDocumentConfirm(null)}>
                        Annuler
                    </Button>
                    <Button type="button" color="danger" onClick={() => removeSavedDocument(deleteDocumentConfirm)}>
                        Supprimer
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal
                isOpen={recordError.open}
                toggle={() => setRecordError({ ...recordError, open: false })}
                backdrop="static"
            >
                <ModalHeader toggle={() => setRecordError({ ...recordError, open: false })}>
                    {t('text.errorTitle')}
                </ModalHeader>
                <ModalBody>
                    <p>{recordError.message}</p>
                    <ModalFooter>
                        <Button
                            type="button"
                            color="light"
                            onClick={() => setRecordError({ ...recordError, open: false })}
                        >
                            {t('actions.close')}
                        </Button>
                    </ModalFooter>
                </ModalBody>
            </Modal>

            <Modal
                isOpen={saveConfirm}
                toggle={() => setSaveConfirm(false)}
                backdrop="static"
            >
                <ModalHeader toggle={() => setSaveConfirm(false)}>
                    Confirmer l'enregistrement
                </ModalHeader>
                <ModalBody>
                    <p className="mb-0">
                        Vous pouvez enregistrer ce vehicule meme si certaines informations ne sont pas encore renseignees.
                        Confirmez-vous l'enregistrement ?
                    </p>
                    <ModalFooter>
                        <Button type="button" color="light" onClick={() => setSaveConfirm(false)}>
                            Annuler
                        </Button>
                        <Button type="button" color="primary" onClick={confirmSave}>
                            Enregistrer
                        </Button>
                    </ModalFooter>
                </ModalBody>
            </Modal>

            {photoViewer.open && photoViewer.photos.length > 0 && (
                <Lightbox
                    mainSrc={photoViewer.photos[photoViewer.index].url}
                    nextSrc={photoViewer.photos[(photoViewer.index + 1) % photoViewer.photos.length]?.url}
                    prevSrc={photoViewer.photos[(photoViewer.index + photoViewer.photos.length - 1) % photoViewer.photos.length]?.url}
                    imageTitle={photoViewer.photos[photoViewer.index].title}
                    onCloseRequest={() => setPhotoViewer(prev => ({ ...prev, open: false }))}
                    onMovePrevRequest={() => setPhotoViewer(prev => ({
                        ...prev,
                        index: (prev.index + prev.photos.length - 1) % prev.photos.length,
                    }))}
                    onMoveNextRequest={() => setPhotoViewer(prev => ({
                        ...prev,
                        index: (prev.index + 1) % prev.photos.length,
                    }))}
                    enableZoom={true}
                />
            )}
        </React.Fragment>
    );
};

export default VehiculeForm;
